import { VisualRandom } from '../core/VisualRandom.js';
import {
  VISUAL_RECIPE_VERSION,
  layer,
  type VisualRecipe,
} from '../recipe/VisualRecipe.js';
import { SPATIAL_MODES, type SpatialMode } from '../wave/SpatialPlanner.js';
import {
  DEFAULT_WAVE_WEIGHTS,
  type WaveMode,
  type WaveStyleWeights,
} from '../wave/WaveDirector.js';

/**
 * A recipe built for waves.
 *
 * `randomRecipe` draws a handful of layers and maps signals to them, which is a
 * *stack*. This builds something different: one layer for every material the
 * build can draw, none of them spawning on their own, and the wave settings that
 * decide which of them answers each disturbance.
 *
 * That difference matters for a reason that is easy to miss. `WEIGHTED_RANDOM`
 * can only choose between materials that have a layer to draw into — the engine
 * offers the director exactly the renderers present in the recipe, because a
 * material with nowhere to go would produce a wave that silently vanishes. So a
 * recipe with two layers gives a six-way weighting table two real options, and
 * the mode looks broken while behaving correctly. Covering the materials is what
 * makes the choice mean anything.
 *
 * The ambient spawners are turned off deliberately. Every material here still
 * emits on its own given the chance — smoke drifts, ripples rise, the brush
 * marks the beat — and with six of them running at once the frame is full before
 * a single wave lands. Quiet layers are what make the wave visible.
 */

export interface WaveRecipeOptions {
  readonly seed: number;
  /** Renderer types this build actually has. */
  readonly available: readonly string[];
  /** Omit to let the seed choose. */
  readonly waveMode?: WaveMode;
  readonly spatialMode?: SpatialMode;
  readonly waveStyles?: WaveStyleWeights;
  /**
   * Drawn underneath, if the build has it.
   *
   * Without a ground the frame is marks on nothing, which reads as a bug rather
   * than as a composition — the same reason `randomRecipe` keeps an anchor.
   */
  readonly anchorRenderer?: string;
}

/** The materials a wave can be made of, in the order they are layered. */
const WAVE_MATERIALS = ['SMOKE', 'WATERCOLOR', 'SUMI', 'RIPPLE', 'MATH', 'CODE'] as const;

/**
 * Placement rules worth generating.
 *
 * `CENTER` is left out: it is the default a recipe gets for *not* asking, and
 * generating it would produce a recipe that turned the whole system on in order
 * to put everything back in the middle.
 */
const GENERATED_SPATIAL: readonly SpatialMode[] = SPATIAL_MODES.filter(
  (mode) => mode !== 'CENTER',
);

export function waveRecipe(options: WaveRecipeOptions): VisualRecipe {
  const random = new VisualRandom(options.seed);
  const anchor = options.anchorRenderer ?? 'MANDALA';
  const layers = [];

  if (options.available.includes(anchor)) {
    layers.push(
      layer({
        id: 'ground',
        renderer: anchor,
        zDepth: 0,
        seed: random.int(0, 1_000_000),
        intensity: 0.9,
        signalMapping: {
          // The ground still answers the music, just not with marks of its own.
          BASS: { target: 'bloomTrim', scale: 0.25, offset: 0.95 },
        },
      }),
    );
  }

  let depth = 1;
  for (const material of WAVE_MATERIALS) {
    if (!options.available.includes(material)) continue;
    layers.push(
      layer({
        id: material.toLowerCase(),
        renderer: material,
        zDepth: depth,
        seed: random.int(0, 1_000_000),
        opacity: 1,
        intensity: 1,
        parameters: {
          // Silence the self-starters. See the note above.
          spawnRate: 0,
          ...(material === 'MATH' || material === 'CODE' ? { scale: 0.5 } : {}),
        },
        // Deliberately empty. A mapping here would give this layer a second way
        // to spawn, and then a wave assigned to some *other* material would
        // still make a mark in this one — which is exactly the confusion the
        // wave layer exists to remove.
        signalMapping: {},
      }),
    );
    depth += 1;
  }

  // Drawn in a fixed order, like every other generated recipe: reordering these
  // three lines changes what every existing seed produces.
  const waveMode: WaveMode =
    options.waveMode ??
    (['WEIGHTED_RANDOM', 'SHUFFLE', 'RANDOM', 'AUDIO_REACTIVE'] as const)[random.int(0, 4)] ??
    'WEIGHTED_RANDOM';
  const spatialMode: SpatialMode =
    options.spatialMode ??
    (GENERATED_SPATIAL[random.int(0, GENERATED_SPATIAL.length)] as SpatialMode);
  const waveStyles = options.waveStyles ?? jitteredWeights(random);

  return {
    version: VISUAL_RECIPE_VERSION,
    seed: options.seed,
    renderMode: 'RANDOM',
    waveMode,
    spatialMode,
    waveStyles,
    layers,
  };
}

/**
 * The default weights, moved around by the seed.
 *
 * Kept as a perturbation rather than a fresh draw so that every generated recipe
 * still has a recognisable balance — smoke and ripples common, text occasional —
 * while no two seeds produce the same mix. A uniform random table would make
 * every seed feel the same on average, which is the opposite of the intent.
 */
function jitteredWeights(random: VisualRandom): WaveStyleWeights {
  const out: Record<string, number> = {};
  for (const [id, weight] of Object.entries(DEFAULT_WAVE_WEIGHTS)) {
    out[id] = Math.max(1, Math.round(weight * random.range(0.5, 1.6)));
  }
  return out;
}
